import { displayName } from '@wodi/domain';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { PatientDetail } from '@wodi/shared';
import { Button, CardGrid, LoadingBlock, Notice, PageHeader, formatShortAge } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { patientDetailQuery } from '../api/patients.ts';
import { can, sessionQuery } from '../api/session.ts';
import { PatientRecordDocuments } from '../documents/patient-record.tsx';
import { PatientReferralsSection } from '../referrals/patient-referrals.tsx';
import { useOnline } from '../hooks/use-online.ts';
import { PatientImagingReports } from '../imaging/patient-imaging-reports.tsx';
import { LabResultsSection } from '../lab/patient-lab-results.tsx';
import { CorrectDetailsForm } from '../patients/correct-details.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import styles from '../patients/patients.module.css';
import {
  HistorySection,
  IdentifiersSection,
  PhonesSection,
  RegistrySection,
  RelationshipsSection,
  Section,
  VisitsSection,
} from '../patients/record-sections.tsx';
import { ConsentSection } from '../patients/consent-section.tsx';
import { PatientPortalSection } from '../portal/patient-portal-section.tsx';
import { PatientPrecautionsSection } from '../infection/patient-precautions.tsx';
import { PatientAppointmentsSection } from '../appointments/patient-appointments.tsx';
import { InsuranceCoverSection } from '../insurance/cover.tsx';
import { DeceasedNotice, PatientBirthsSection } from '../vital-events/patient-vital-events.tsx';

const route = getRouteApi('/_app/patients/$patientId');

/**
 * Casualty (docs/plans/phase2-casualty.md §5; ADR 0024): an unknown patient's record says
 * so and offers "Identify patient"; a merged one points to the identified patient's record,
 * where its history is shown.
 */
function IdentityNotice({
  patient,
  canIdentify,
}: {
  patient: PatientDetail;
  canIdentify: boolean;
}) {
  if (patient.mergedInto !== null) {
    return (
      <Notice
        tone="info"
        action={
          <Link to="/patients/$patientId" params={{ patientId: patient.mergedInto.patientId }}>
            Open that record
          </Link>
        }
      >
        {`This record was merged into ${patient.mergedInto.displayName} (${patient.mergedInto.patientNumber}) on ${formatInstant(patient.mergedInto.mergedAt)}. Everything recorded here is shown there.`}
      </Notice>
    );
  }
  if (patient.identity.state !== 'unknown') return null;
  return (
    <Notice
      tone="info"
      {...(canIdentify && patient.status === 'active'
        ? {
            action: (
              <Link to="/patients/$patientId/identify" params={{ patientId: patient.id }}>
                Identify patient
              </Link>
            ),
          }
        : {})}
    >
      {`This patient is not identified yet.${patient.unknownDescription === null ? '' : ` Description: ${patient.unknownDescription}`}`}
    </Notice>
  );
}

function residenceText(patient: PatientDetail): string {
  const r = patient.residence;
  if (r === null) return 'Not recorded';
  const parts = [r.locality, r.ward?.display, r.subCounty?.display, r.county?.display].filter(
    (p): p is string => p !== null && p !== undefined && p !== '',
  );
  return parts.length === 0 ? 'Not recorded' : parts.join(', ');
}

function DetailsSection({ patient, canUpdate }: { patient: PatientDetail; canUpdate: boolean }) {
  const [correcting, setCorrecting] = useState(false);
  const online = useOnline();
  const name = [patient.name.given, patient.name.middle, patient.name.family]
    .filter(Boolean)
    .join(' ');
  return (
    <Section
      title="Details"
      {...(canUpdate && !correcting
        ? {
            action: (
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  setCorrecting(true);
                }}
              >
                Correct details
              </Button>
            ),
          }
        : {})}
    >
      {correcting ? (
        <CorrectDetailsForm
          patient={patient}
          onDone={() => {
            setCorrecting(false);
          }}
        />
      ) : (
        <dl className={styles.details}>
          <dt>Name</dt>
          <dd>{name}</dd>
          <dt>Sex</dt>
          <dd>{patient.sex.display}</dd>
          <dt>Date of birth</dt>
          <dd>
            {patient.birthDate === null
              ? 'Not recorded'
              : patient.birthDateEstimated
                ? `Estimated, ${formatShortAge(patient.age, true)}`
                : `${formatIsoDate(patient.birthDate)}, ${formatShortAge(patient.age, false)}`}
          </dd>
          <dt>Patient number</dt>
          <dd className="wodi-id">{patient.patientNumber}</dd>
          <dt>Registered at</dt>
          <dd>{patient.registeredFacility.name}</dd>
          <dt>Residence</dt>
          <dd>{residenceText(patient)}</dd>
        </dl>
      )}
    </Section>
  );
}

/**
 * A patient's record (plan §6.5): banner first, then details, identifiers, phones,
 * relationships, consent, registry status, visits and history. Corrections never delete:
 * they add a version or mark a row entered in error, with a reason.
 */
export function PatientPage() {
  const { patientId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const detail = useQuery(patientDetailQuery(patientId));
  const canUpdate = session !== null && can(session, 'patients.update');
  const canCheckIn = session !== null && can(session, 'visits.open');
  const canSeeVisits = session !== null && can(session, 'visits.read');
  const canLogRequest = session !== null && can(session, 'records.requests.manage');
  const canReadAccessLog = session !== null && can(session, 'records.access_log.read');
  const canReadChart = session !== null && can(session, 'records.charts.read');
  const canReadMaternity = session !== null && can(session, 'maternity.read');
  const canReadChildHealth = session !== null && can(session, 'mch.read');
  const patient = detail.data;

  return (
    <div className={styles.record}>
      <PatientBannerLoader patientId={patientId} linked={false} />
      <PageHeader
        title="Patient record"
        {...(canCheckIn ||
        canLogRequest ||
        canReadAccessLog ||
        canReadChart ||
        canReadMaternity ||
        canReadChildHealth
          ? {
              actions: (
                <div className={styles.headerLinks}>
                  {canCheckIn ? (
                    <Link to="/reception" search={{ patientId }}>
                      Check in
                    </Link>
                  ) : null}
                  {/* Data-subject requests (docs/plans/phase2-records-data-rights.md §6). */}
                  {canLogRequest ? (
                    <Link to="/patients/$patientId/records-requests/new" params={{ patientId }}>
                      Records request
                    </Link>
                  ) : null}
                  {canReadAccessLog ? (
                    <Link to="/patients/$patientId/access-log" params={{ patientId }}>
                      Access log
                    </Link>
                  ) : null}
                  {/* Chart tracking (docs/plans/phase2-medical-records-charts.md §6). */}
                  {canReadChart ? (
                    <Link to="/patients/$patientId/chart" params={{ patientId }}>
                      Chart
                    </Link>
                  ) : null}
                  {/* Maternity and MCH (docs/plans/phase2-maternity-mch.md §6). */}
                  {canReadMaternity ? (
                    <Link to="/patients/$patientId/maternity" params={{ patientId }}>
                      Maternity
                    </Link>
                  ) : null}
                  {canReadChildHealth ? (
                    <Link to="/patients/$patientId/child-health" params={{ patientId }}>
                      Child health
                    </Link>
                  ) : null}
                </div>
              ),
            }
          : {})}
      />
      {detail.isPending ? <LoadingBlock label="Loading the patient's record" rows={6} /> : null}
      {detail.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void detail.refetch()}>
              Try again
            </Button>
          }
        >
          {detail.error instanceof ApiError
            ? detail.error.message
            : "The patient's record couldn't be loaded. Try again."}
        </Notice>
      ) : null}
      {patient === undefined ? null : (
        <>
          {patient.status === 'entered_in_error' ? (
            <Notice tone="info">
              {`This record was entered in error${patient.statusReason === null ? '' : `: ${patient.statusReason}`}. It is kept for the history.`}
            </Notice>
          ) : null}
          <DeceasedNotice
            deceased={patient.deceased}
            canReadDeaths={session !== null && can(session, 'deaths.read')}
          />
          <IdentityNotice
            patient={patient}
            canIdentify={session !== null && can(session, 'patients.merge.request')}
          />
          {/* The short facts side by side, filling the width (ADR 0019); the long lists
              (results, documents, visits, history) keep a full row each below. */}
          <CardGrid columns={3}>
            <DetailsSection patient={patient} canUpdate={canUpdate} />
            <IdentifiersSection patient={patient} canUpdate={canUpdate} />
            <PhonesSection patient={patient} canUpdate={canUpdate} />
            <RelationshipsSection patient={patient} canUpdate={canUpdate} canCheckIn={canCheckIn} />
            <ConsentSection
              patient={patient}
              canUpdate={canUpdate}
              canReadWording={session !== null && can(session, 'patients.register')}
            />
            <RegistrySection patient={patient} canUpdate={canUpdate} />
          </CardGrid>
          <PatientPrecautionsSection
            patientId={patientId}
            mayFlag={session !== null && can(session, 'infection.flag')}
          />
          <InsuranceCoverSection
            patientId={patientId}
            canManage={session !== null && can(session, 'insurance.coverage.manage')}
          />
          {session !== null && can(session, 'lab.results.read') ? (
            <LabResultsSection patient={patient} />
          ) : null}
          {session !== null && can(session, 'imaging.results.read') ? (
            <PatientImagingReports patient={patient} />
          ) : null}
          <PatientRecordDocuments patientId={patientId} />
          <PatientReferralsSection patientId={patientId} />
          {session !== null && can(session, 'appointments.read') ? (
            <PatientAppointmentsSection
              patient={{
                id: patientId,
                displayName: displayName(patient.name),
              }}
            />
          ) : null}
          {session !== null && can(session, 'births.read') ? (
            <PatientBirthsSection patientId={patientId} />
          ) : null}
          {/* The patient portal and feedback (docs/plans/phase3-portal-feedback-kiswahili.md §7.2). */}
          {session !== null ? (
            <PatientPortalSection patientId={patientId} session={session} />
          ) : null}
          {canSeeVisits ? <VisitsSection patientId={patientId} /> : null}
          <HistorySection patientId={patientId} />
        </>
      )}
    </div>
  );
}
